import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { queryClient } from './lib/query-client';
import { initSentry, instrumentRouter, Sentry } from './lib/sentry';
import './styles/globals.css';

initSentry();

/**
 * Mobile Safari は新 deploy 後も bfcache (back-forward cache) で旧バンドルを
 * 持ち続けることがある。タブ切替 → 戻った瞬間 (`pageshow` の `persisted=true`) に
 * 一度だけ静かにリロードしてバンドル鮮度を保つ。
 *
 * 副作用最小:
 * - バナー / トースト無し
 * - polling 無し
 * - ユーザー操作中には発火しない (bfcache 復元タイミングのみ)
 * - SSR でも no-op に近い (persisted は通常 false)
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });
}

/**
 * TanStack Router instance.
 *
 * Type registration via the `Register` interface is required so that
 * `<Link to="/...">` elsewhere in the app gets autocompletion + compile-time
 * verification of route paths.
 *
 * `instrumentRouter` is called as a side-effect (returns the same instance)
 * so the inferred `router` type retains the full `routeTree` information
 * needed by `Register`.
 */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

instrumentRouter(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

const Root = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);

// Sentry's React error boundary attaches transparently when the SDK is
// initialised; it's a no-op when the DSN is empty.
const tree =
  Sentry.getClient() !== undefined ? (
    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>{Root}</Sentry.ErrorBoundary>
  ) : (
    Root
  );

createRoot(rootEl).render(tree);
